<script>
    import { getContext } from 'svelte';
    import { Policy } from "@Store";
    import Question from "@components/Question.svelte";
    import 'animate.css';
    import { computeOperationsValid } from '@routes/validators/validator';

    const CONTEXT = getContext('CONTEXT');

    $: questions = $Policy.questions ? $Policy.questions : [];
    $: valid = $Policy.valid;

    // APPLICANT QUESTIONS SECTION

    // Create a lookup map in one pass - much more efficient!
    $: questionMap = questions.reduce((map, question) => {
        map[question.question] = question;
        return map;
    }, {});

    // Now you can access any question efficiently
    $: ({
        'Is your business': business_type_question,
        'Please provide full details of your personal operations and services provided:': operations_question,
        'Do you provide any of the following services:': services_question,
        'Select one of the following': acupuncturist_question,
        'Do you carry a separate policy for your Registered Acupuncture operations?': seperate_policy,
        'Would you like to include your Registered Acupuncture operations on this policy?': registed_acu, 
        'Name of Acupuncture Organization that regulates your practice': organization_question,
        'Are you a member in good standing of the above organization': member_standing_question,
        'Does the Applicant use Single Use Needles': single_use_needles_question,
        'Does the applicant practice any modalities other than as an acupuncturist within the scope of a Traditional Chinese Medicine Practitioner': other_modalities_question,
        'Please describe': other_modalities_description_question,
        'Insurer': insurer_question,
        'Expiry Date': expiry_date_question,
        'Retroactive Date (if applicable, if date is unknown, please supply date licensed as an ND)': retroactive_date_question,
        'Actual anticipated revenue for your Current Fiscal Year': revenue_question,
        'Anticipated for your Current Fiscal Year:': anticipated_question,

        'Full time': full_time_employees_question,
        'Part time': part_time_employees_question,
        'Volunteers': volunteers_employees_question,
        'Contractors': contract_employees_question,

        '% Revenue derived from Canada': canada_revenue_question,
        '% Revenue derived from outside Canada': outside_canada_revenue_question,
    } = questionMap);

    // Create named values lookup map - ONE iteration instead of 9!
    $: namedValuesMap = ($Policy.saved_named_values || []).reduce((map, nv) => {
        map[nv.name] = nv.value?.trim() || '';
        return map;
    }, {});

    // Helper function to check if a named value exists and is not empty
    const isAnswered = (map, key) => !!map[key];

    $: getAnswerText = (q) => (q && $Policy.answers?.[q.id]?.text_answer || '').trim();
    $: isAnsweredById = (q) => !!getAnswerText(q);

    $: ask_for_description = getAnswerText(other_modalities_question) === 'yes';

    $: is_registered_acupuncturist = getAnswerText(acupuncturist_question) === 'I am a registered acupuncturist';
    $: is_seperate = getAnswerText(seperate_policy) === 'no';
    $: registed_acu_yes = getAnswerText(registed_acu) == 'yes';
    // Check if Acupuncture/Chinese Therapy is selected - using answer directly
    $: showAcupuncturistQuestion = (() => {
        if (!services_question || !$Policy.answers) return false;
        
        const servicesAnswer = $Policy.answers[services_question.id];
        if (!servicesAnswer) return false;
        
        const answerText = servicesAnswer.text_answer || '';
        return answerText.includes('Acupuncture / Chinese Therapy');
    })();

    // ONCE ALL THE ABOVE ARE ANSWERED, SET VALID TO TRUE
    $: operations_valid = computeOperationsValid($Policy);
        
    // Update CONTEXT when validation changes
    $: {
        CONTEXT.routeValidation.update(validation => ({
            ...validation,
            operations: operations_valid
        }));
    }

    function handleAnswer(e) {
        CONTEXT.handleAnswer(e);
    }


</script>

<div class="page-container">
    <div class="questions-wrapper">
        <!-- APPLICANT QUESTIONS -->
        {#if business_type_question}
            <div class="animate__animated animate__fadeInUp">
                <Question question={business_type_question} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
            </div>
        {/if}
        {#if business_type_question}
            <div class="animate__animated animate__fadeInUp">
                <Question question={operations_question} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
            </div>
        {/if}
        {#if services_question}
            <div class="animate__animated animate__fadeInUp">
                <Question question={services_question} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
            </div>
        {/if}
        {#if showAcupuncturistQuestion}
            <div class="animate__animated animate__fadeInUp">
                <Question question={acupuncturist_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
            </div>
            {#if is_registered_acupuncturist}
                {#if seperate_policy }
                <div class="animate__animated animate__fadeInUp">
                    <Question question={seperate_policy} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                </div>
                {/if}
                {#if is_seperate}
                    {#if registed_acu}
                        <div class="animate__animated animate__fadeInUp">
                            <Question question={registed_acu} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                        </div>
                    {/if}
                    {#if registed_acu_yes}
                        {#if organization_question}
                            <div class="animate__animated animate__fadeInUp">
                                <Question question={organization_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                            </div>
                        {/if}
                        {#if member_standing_question}
                            <div class="animate__animated animate__fadeInUp">
                                <Question question={member_standing_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                            </div>
                        {/if}
                        {#if single_use_needles_question}
                            <div class="animate__animated animate__fadeInUp">
                                <Question question={single_use_needles_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                            </div>
                        {/if}
                        {#if other_modalities_question}
                            <div class="animate__animated animate__fadeInUp">
                                <Question question={other_modalities_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                            </div>
                        {/if}

                        {#if other_modalities_description_question && ask_for_description}
                            <div class="animate__animated animate__fadeInUp">
                                <Question question={other_modalities_description_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                            </div>
                        {/if}

                        {#if insurer_question && expiry_date_question && retroactive_date_question}
                            <div class="section-container animate__animated animate__fadeInUp">
                                <div class="section-header">
                                    <div class="section-icon">📍</div>
                                    <h1>Previous Acupuncture Coverage</h1>
                                </div>
                                
                                <div class="sub-questions">
                                    <div class="sub-question">
                                        <Question question={insurer_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                                    </div>

                                    <div class="sub-question">
                                        <Question question={expiry_date_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                                    </div>
                                    <div class="sub-question">
                                        <Question question={retroactive_date_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                                    </div>
                                </div>
                            </div>
                        {/if}
                    {/if}
                {/if}
            {/if}

        {/if}

                <!-- here we will put in optional questions -->
        {#if revenue_question && anticipated_question}
            <div class="section-container animate__animated animate__fadeInUp">
                <div class="section-header">
                    <div class="section-icon">💰</div>
                    <h1>Gross Annual Revenue for all services provided</h1>
                </div>
                
                <div class="sub-questions">

                        <div class="sub-question">
                            <Question question={anticipated_question} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                        </div>
                        <div class="sub-question">
                            <Question question={revenue_question} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                        </div>
                </div>
            </div>
        {/if}
        {#if full_time_employees_question && part_time_employees_question && contract_employees_question && volunteers_employees_question}
            <div class="section-container animate__animated animate__fadeInUp">
                <div class="section-header">
                    <div class="section-icon">👩🏻‍🔧</div>
                    <h1>Number of Employees</h1>
                </div>
                
                <div class="sub-questions">
                    <div class="sub-question">
                        <Question question={full_time_employees_question} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                    </div>
                    <div class="sub-question">
                        <Question question={part_time_employees_question} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                    </div>
                    <div class="sub-question">
                        <Question question={contract_employees_question} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                    </div>
                    <div class="sub-question">
                        <Question question={volunteers_employees_question} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                    </div>
                </div>
            </div>
        {/if}

        {#if canada_revenue_question && outside_canada_revenue_question}
            <div class="animate__animated animate__fadeInUp">
                    <Question question={canada_revenue_question} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                </div>
            <div class="animate__animated animate__fadeInUp">
                <Question question={outside_canada_revenue_question} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
            </div>
        {/if}

        

    </div>
</div>

<style>
    .page-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 0 1rem;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    .questions-wrapper {
        width: 100%;
        max-width: 500px;
        margin-bottom: 2rem;
    }

    /* Section Container Styles */
    .section-container {
        width: 100%;
        background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
        border: 2px solid #e9ecef;
        border-radius: 20px;
        box-sizing: border-box;
        padding: 2rem 1.5rem;
        margin: 2rem 0;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
    }

    .section-header {
        display: flex;
        align-items: center;
        gap: 1rem;
        padding-bottom: 1.5rem;
        border-bottom: 2px solid #e9ecef;
    }

    .section-icon {
        font-size: 2rem;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 56px;
        height: 56px;
        background: linear-gradient(135deg, #fff3cd 0%, #fff8e1 100%);
        border-radius: 16px;
        flex-shrink: 0;
        box-shadow: 0 2px 8px rgba(255, 193, 7, 0.15);
    }

    .section-header h1 {
        font-size: 1.375rem;
        font-weight: 600;
        color: #1d1d1f;
        margin: 0;
        line-height: 1.3;
        letter-spacing: -0.01em;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    .sub-questions {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
    }

    .sub-question {
        position: relative;
    }

    @media (max-width: 768px) {
        .section-container {
            padding: 1.5rem 1rem;
            border-radius: 16px;
        }

        .section-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.75rem;
        }

        .section-header h1 {
            font-size: 1.25rem;
        }

        .section-icon {
            width: 48px;
            height: 48px;
            font-size: 1.75rem;
        }

        .sub-question {
            padding-left: 1rem;
        }

        .sub-question::before {
            width: 3px;
        }
    }
</style>